import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import { loadPagesForParent } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateLoadParameter from "@insite/client-framework/Store/Pages/NewsList/Handlers/UpdateLoadParameter";
import translate from "@insite/client-framework/Translate";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import convertToNumberIfString from "@insite/mobius/utilities/convertToNumberIfString";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import NewsCard from "@stoneage/App/Components/NewsPageTile";
import Notify from "@stoneage/App/Components/Notify";
import { color, xScreen } from "@stoneage/XDL/Utilities";
import parse from "html-react-parser";
import sortBy from "lodash/sortBy";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    defaultPageSize = "defaultPageSize",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.defaultPageSize]: number;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: WidgetProps) => {
    const pages = getDataView(state.data.pages, { parentNodeId: getCurrentPage(state).nodeId });
    let links: SafeDictionary<PageLinkModel> = {};
    if (pages.value) {
        links = pages.value.reduce(
            (links, page) => ({ ...links, [page.nodeId]: getPageLinkByNodeId(state, page.nodeId) }),
            {},
        );
    }
    return {
        parentNodeId: getCurrentPage(state).nodeId,
        pageTitle: getCurrentPage(state)?.fields["title"],
        location: getLocation(state),
        pages,
        links,
        parameter: state.pages.newsList.getNewsPagesParameters[getCurrentPage(state).nodeId] ?? {
            parentNodeId: getCurrentPage(state).nodeId,
        },
    };
};

const mapDispatchToProps = {
    loadPagesForParent,
    updateLoadParameter,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface NewsListViewStyles {
    titleText?: TypographyProps;
    authorText?: TypographyProps;
    publishDateText?: TypographyProps;
    listWrapper?: InjectableCss;
    newsWrapper?: InjectableCss;
    summaryWrapper?: InjectableCss;
    dateAndAuthorWrapper?: InjectableCss;
    readMoreLink?: LinkPresentationProps;
    pagination?: PaginationPresentationProps;

    wrapper?: InjectableCss;
    infoMessage?: TypographyPresentationProps;
    titleWrapper?: InjectableCss;
    title?: TypographyProps;
    totalProducts?: TypographyProps;
    topPaginationWrapper?: InjectableCss;
    selectWrapper?: InjectableCss;
    select?: SelectPresentationProps;
}

export const newsListViewStyles: NewsListViewStyles = {
    topPaginationWrapper: {
        css: css`
            display: flex;
            align-items: center;
            padding-top: 12px;
            margin-bottom: 32px;
            border-top: 1px solid ${color("common.ui6")};
            @media (max-width: 767px) {
                flex-wrap: wrap;
                justify-content: space-between;
            }
        `,
    },
    pagination: {
        cssOverrides: {
            pagination: css`
                flex-grow: 1;
                justify-content: space-between;
                @media (max-width: 700px) {
                    justify-content: flex-end !important;
                }
            `,
            linkList: css`
                display: flex !important;
                @media (max-width: 700px) {
                    display: none !important;
                }
            `,
        },
    },
    selectWrapper: {
        css: css`
            margin-right: 22px;
            & .form-input-wrapper {
                width: 143px;
            }
            && select {
                height: 26px;
            }
            && select + span {
                width: 22px;
                top: -2px;
            }

            && label {
                padding: 0 8px 0 0;
            }
        `,
    },
    select: {
        mergeCss: true,
        css: css`
            & > span {
                width: 22px;
            }
        `,
        labelProps: {
            css: css`
                width: unset;
            `,
        },
        cssOverrides: {
            formField: css`
                & > span {
                    width: 22px;
                }
            `,
        },
    },
    infoMessage: {
        color: "text.main",
        size: 12,
        lineHeight: "18px",
        css: css`
            display: block;
            margin-bottom: 6px;
        `,
    },
    titleWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            margin-bottom: 24px;
            ${xScreen("lte", "sm")} {
                flex-wrap: wrap;
            }
        `,
    },
    title: {
        variant: "h1",
        lineHeight: "30px",
        css: css`
            margin-bottom: 0;
            margin-right: 32px;
            ${xScreen("lte", "xs")} {
                margin-right: 0;
                margin-bottom: 12px;
            }
        `,
    },
    totalProducts: {
        size: "18px",
        lineHeight: "21px",
        weight: "bold",
        color: "common.black",
        css: css`
            ${xScreen("lte", "xs")} {
                width: 100%;
                display: block;
            }
        `,
    },
    newsWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            margin-right: -22px;
            @media (max-width: 650px) {
                margin-right: 0;
            }
        `,
    },
};

const bottomPaginationStyles = {
    pagination: {
        cssOverrides: {
            pagination: css`
                justify-content: flex-end;
                margin-top: 10px !important;
            `,
        },
    },
};

const styles = newsListViewStyles;

type State = {
    sortType: "Alphabetical" | "Newest" | "Oldest";
};

const sortByDropdown = [
    {
        displayName: "Alphabetical",
        value: "Alphabetical",
    },
    {
        displayName: "Newest First",
        value: "Newest",
    },
    {
        displayName: "Oldest First",
        value: "Oldest",
    },
];

const today = new Date().getTime();

const comparePublishDate = (page: PageProps) => {
    const publishDate = new Date(page.fields["publishDate"]).getTime();

    return publishDate <= today;
};

const compareExpirationDate = (page: PageProps) => {
    const expirationDate = new Date(page.fields["expirationDate"]).getTime();

    return expirationDate > today;
};

class NewsListView extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            sortType: "Newest",
        };
    }

    UNSAFE_componentWillMount() {
        if (!this.props.pages.value && !this.props.pages.isLoading) {
            this.props.loadPagesForParent(this.props.parameter);
        }
        this.checkQueryString();
    }

    componentDidUpdate() {
        if (!this.props.pages.value && !this.props.pages.isLoading) {
            this.props.loadPagesForParent(this.props.parameter);
        }
        this.checkQueryString();
    }

    changePage = (newPageIndex: number) => {
        this.updateQueryString(newPageIndex, this.props.parameter.pageSize);
    };

    changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        this.updateQueryString(1, newPageSize);
    };

    changeSortType = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const { search } = this.props.location;
        const pageSize =
            parseQueryString<{ page?: number; pageSize?: number; sortType?: string }>(search)?.["pageSize"];
        this.setState({ sortType: event.currentTarget.value as "Alphabetical" | "Newest" | "Oldest" });
        this.updateQueryString(1, pageSize, event.currentTarget.value);
    };

    updateQueryString(page: number, pageSize?: number, sortType?: string) {
        const {
            history,
            location: { pathname },
        } = this.props;
        let newLocation = `${pathname}?page=${page}`;
        if (pageSize) {
            newLocation += `&pageSize=${pageSize}`;
        }
        newLocation += `&sortBy=${sortType || this.state.sortType}`;
        history.push(newLocation);
    }

    checkQueryString() {
        const {
            location: { search },
            parameter: { page, pageSize, parentNodeId },
        } = this.props;
        const parsedQueryString = parseQueryString<{ page?: string; pageSize?: string; sortBy?: string }>(search);
        const newPage = parsedQueryString.page ? convertToNumberIfString(parsedQueryString.page) : undefined;
        const newPageSize = parsedQueryString.pageSize
            ? convertToNumberIfString(parsedQueryString.pageSize)
            : undefined;
        const newSortType = parsedQueryString.sortBy ? parsedQueryString.sortBy : this.state.sortType;
        if (newPage !== page || newPageSize !== pageSize) {
            this.props.updateLoadParameter({
                page: newPage,
                pageSize: newPageSize,
                parentNodeId,
            });
        }

        if (newSortType !== this.state.sortType) {
            this.setState({ sortType: newSortType as "Alphabetical" | "Newest" | "Oldest" });
        }
    }

    render() {
        const {
            links,
            pages: { value: pages },
            fields: { defaultPageSize },
            parameter,
            pageTitle,
        } = this.props;
        const { sortType } = this.state;

        if (!pages || !pages.length) {
            return (
                <StyledWrapper
                    css={css`
                        height: 300px;
                        width: 100%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    `}
                >
                    <Notify messageType="info" body={siteMessage("ResourceCenter_NoArticlesFound")} />
                </StyledWrapper>
            );
        }

        let sortedAndPagedPages = sortBy(pages, o =>
            sortType === "Alphabetical" ? o.fields["title"] : o.fields["publishDate"],
        );
        if (sortType === "Newest") {
            sortedAndPagedPages.reverse();
        }

        sortedAndPagedPages = sortedAndPagedPages.filter(page => comparePublishDate(page));
        sortedAndPagedPages = sortedAndPagedPages.filter(page => compareExpirationDate(page));

        const totalItems = sortedAndPagedPages.length;
        const page = parameter.page ?? 1;
        const pageSize = parameter.pageSize ?? defaultPageSize;
        const numberOfPages = totalItems / pageSize;

        sortedAndPagedPages = sortedAndPagedPages.slice((page - 1) * pageSize, page * pageSize);

        return (
            <StyledWrapper {...styles.listWrapper}>
                <StyledWrapper {...styles.wrapper}>
                    <StyledWrapper {...styles.titleWrapper}>
                        <Typography as="h1" {...styles.title}>
                            {pageTitle}
                        </Typography>
                        <Typography {...styles.totalProducts}>{translate("{0} Items", `${totalItems}`)}</Typography>
                    </StyledWrapper>
                    <Typography {...styles.infoMessage}>
                        {translate(
                            "Showing {0}-{1} of {2} Items",
                            `${(page - 1) * pageSize + 1}`,
                            `${
                                page !== numberOfPages
                                    ? page * pageSize <= totalItems
                                        ? page * pageSize
                                        : totalItems
                                    : totalItems
                            }`,
                            `${totalItems}`,
                        )}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.topPaginationWrapper}>
                    <StyledWrapper {...styles.selectWrapper} className="select-wrapper">
                        <Select
                            {...styles.select}
                            label={translate("Sort by")}
                            iconProps={{
                                size: 2,
                                src: "IconArrowDropdownSmall",
                            }}
                            value={sortType}
                            labelPosition="left"
                            onChange={this.changeSortType}
                        >
                            {sortByDropdown.map(option => (
                                <option value={option.value} key={uniqueId()}>
                                    {option.displayName}
                                </option>
                            ))}
                        </Select>
                    </StyledWrapper>
                    {totalItems > defaultPageSize && (
                        <Pagination
                            resultsCount={totalItems}
                            currentPage={page}
                            resultsPerPage={pageSize}
                            resultsPerPageOptions={[
                                defaultPageSize,
                                defaultPageSize * 2,
                                defaultPageSize * 3,
                                defaultPageSize * 4,
                            ]}
                            onChangePage={this.changePage}
                            onChangeResultsPerPage={this.changeResultsPerPage}
                            pageSizeCookie="NewsList-PageSize"
                            {...styles.pagination}
                            {...{ isBottom: false }}
                        />
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.newsWrapper}>
                    {sortedAndPagedPages.map((page: PageProps) => (
                        <NewsCard
                            key={page.id}
                            title={page.fields["title"]}
                            image={page.fields["openGraphImage"]}
                            author={page.fields["author"]}
                            publishDate={page.fields["publishDate"]}
                            content={page.fields["newsSummary"] || page.fields["newsContent"]}
                            link={links[page.nodeId]?.url}
                        />
                    ))}
                </StyledWrapper>
                {totalItems > defaultPageSize && (
                    <Pagination
                        resultsCount={totalItems}
                        currentPage={page}
                        resultsPerPage={pageSize}
                        resultsPerPageOptions={[
                            defaultPageSize,
                            defaultPageSize * 2,
                            defaultPageSize * 3,
                            defaultPageSize * 4,
                        ]}
                        onChangePage={this.changePage}
                        onChangeResultsPerPage={this.changeResultsPerPage}
                        pageSizeCookie="NewsList-PageSize"
                        {...bottomPaginationStyles.pagination}
                    />
                )}
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(NewsListView)),
    definition: {
        group: "News List",
        allowedContexts: ["NewsListPage"],
        fieldDefinitions: [
            {
                name: fields.defaultPageSize,
                editorTemplate: "IntegerField",
                defaultValue: 5,
                fieldType: "General",
                min: 1,
                max: 20,
            },
        ],
    },
};

export default widgetModule;
